<template>
  <div class="watermark-container" ref="refParent">
    <slot></slot>
  </div>
</template>

<script>
import { getStyleStr } from '@/utils/base';
import { getDPR } from '@/utils/dom';
import useClips, { FontGap } from './useClips';
import {apiEnv} from '@/config';

export default {
  name: 'Watermark',
  props: {
    content: {type: [String, Array], default: '黑牛科技'},  //水印文本内容
    image: String,  //水印图片源
    width: { width: Number}, //水印的宽度， content 的默认值是它自己的宽度
    height: { width: Number}, //水印的高度， content 的默认值是它自己的高度
    font: { type: Object}, //字体样式
    rotate: { type: Number, default: -22 }, //水印的旋转角度, 单位 °
    gap: { type: Array, default: () => [100, 100] }, //水印之间的间距
    offset: { type: Array}, //水印从容器左上角的偏移
    zIndex: {type: Number, default: 10}, //追加水印的 z-index
  },
  data(){
    return {
      div: null,
      stopObservation: false, //阻止水印观察
    }
  },
  computed: {
    // 生成水印图片所需 canvas 参数
    color(){return this.font?.color ?? 'rgba(0, 0, 0, .1)'},
    fontSize(){return this.font?.fontSize ?? 16},
    fontWeight(){return this.font?.fontWeight ?? 'normal'},
    fontStyle(){return this.font?.fontStyle ?? 'normal'},
    fontFamily(){return this.font?.fontFamily ?? 'sans-serif'},
    textAlign(){return this.font?.textAlign ?? 'center'},
    textBaseline(){return this.font?.textBaseline ?? 'hanging'},
    gapX(){return this.gap[0]},
    gapY(){return this.gap[1]},

    // 水印的遮罩容器-相关参数
    gapXCenter(){return this.gapX / 2},
    gapYCenter(){return this.gapY / 2},
    offsetLeft(){return this.offset?.[0] ?? this.gapXCenter},
    offsetTop(){return this.offset?.[1] ?? this.gapYCenter},
  },
  watch: {
    props: {
      deep: true,
      // immediate: true,
      handler(){
        this.renderWatermark();
      },
    }
  },
  mounted() {
    if (apiEnv === 'prod') this.renderWatermark();

    // 创建观察器对象,被篡改后，再重置水印
    this.ob = new MutationObserver(entries => {
      if (this.stopObservation) return;
      entries.forEach(entry => {
        if (this.reRendering(entry, this.div)){
          this.destroyWatermark();
          this.renderWatermark();
        }
      })
    });
    // 添加观察目标
    this.ob.observe(this.$refs.refParent, {
      childList: true,  //观察父元素的所有子节点变化
      subtree: true,  //观察子节点的子节点变化
      attributes: true, //观察属性变化
    })
  },
  beforeDestroy(){
    this.ob.disconnect();
    this.destroyWatermark();
  },
  methods: {
    // 水印是否被篡改
    reRendering(mutation, watermarkElement){
      let flag = false;
      // 处理删除
      if (mutation.removedNodes.length && watermarkElement) {
        flag = Array.from(mutation.removedNodes).includes(watermarkElement);
      }
      // 处理属性修改
      if (mutation.type === 'attributes' && mutation.target === watermarkElement) {
        flag = true;
      }
      return flag;
    },
    // 获取水印遮罩样式
    getMarkStyle(){
      const markStyle = {
        zIndex: this.zIndex,
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        backgroundRepeat: 'repeat'
      };
      // 计算偏移的样式
      let positionLeft = this.offsetLeft - this.gapXCenter;
      let positionTop = this.offsetTop - this.gapYCenter;

      if (positionLeft > 0) {
        markStyle.left = `${positionLeft}px`
        markStyle.width = `calc(100% - ${positionLeft}px)`
        positionLeft = 0
      }
      if (positionTop > 0) {
        markStyle.top = `${positionTop}px`
        markStyle.height = `calc(100% - ${positionTop}px)`
        positionTop = 0
      }
      markStyle.backgroundPosition = `${positionLeft}px ${positionTop}px`;

      return markStyle;
    },
    // 获取水印大小，默认：图片：[120,64]；内容：按内容计算；
    getMarkSize(ctx){
      let defaultWidth = 120;
      let defaultHeight = 64;
      const image = this.image;
      const content = this.content;
      const width = this.width;
      const height = this.height;

      if (!image && ctx.measureText) {
        ctx.font = `${Number(this.fontSize)}px ${this.fontFamily}`;
        const contents = Array.isArray(content) ? content : [content];
        // 获取每一行文本的宽高
        const sizes = contents.map((text) => {
          const metrics = ctx.measureText(text);
          return [
            metrics.width,
            // fontBoundingBoxAscent 水平线到渲染文本的所有字体的矩形最高边界顶部的距离
            // fontBoundingBoxDescent 水平线到渲染文本的所有字体的矩形边界最底部的距离
            metrics.fontBoundingBoxAscent !== undefined
              ? metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent
              : metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent,
          ]
        })
        defaultWidth = Math.ceil(Math.max(...sizes.map((size) => size[0])));
        defaultHeight = Math.ceil(Math.max(...sizes.map((size) => size[1]))) * contents.length + (contents.length - 1) * FontGap;
      }
      return [width ?? defaultWidth, height ?? defaultHeight];
    },
    // getClips(){return useClips();},
    // 渲染水印
    renderWatermark(){
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      if (!this.div) this.div = document.createElement('div');  //防止多次调用重复生成

      const image = this.image;
      const content = this.content;
      const getClips = useClips();
      const size = this.getMarkSize(ctx);

      const drawCanvas = (drawContent) => {
        const paramsArr = {
          content: drawContent || '',
          rotate: this.rotate,
          ratio: getDPR(),
          width: size[0],
          height: size[1],
          font: {
            color: this.color,
            fontSize: this.fontSize,
            fontStyle: this.fontStyle,
            fontWeight: this.fontWeight,
            fontFamily: this.fontFamily,
            textAlign: this.textAlign,
            textBaseline: this.textBaseline,
          },
          gapX: this.gapX,
          gapY: this.gapY,
        }
        const [base64Url, clipWidth] = getClips(paramsArr);
        this.appendWatermark(base64Url, clipWidth);
      }

      if (image) {
        const img = new Image()
        img.onload = () => {
          drawCanvas(img)
        }
        img.onerror = () => {
          drawCanvas(content)
        }
        img.crossOrigin = 'anonymous'
        img.referrerPolicy = 'no-referrer'
        img.src = image
      } else {
        drawCanvas(content)
      }
    },
    // 插入水印
    appendWatermark(base64Url, markWidth){
      const {refParent} = this.$refs;
      if (!(refParent && this.div)) return;
      this.stopObservation = true;  //渲染时，先停止观测

      const styles = getStyleStr({
        ...this.getMarkStyle(),
        backgroundImage: `url('${base64Url}')`,
        backgroundSize: `${Math.floor(markWidth)}px`,
      });
      this.div.setAttribute('style', styles);
      refParent?.append(this.div);

      setTimeout(() => {this.stopObservation = false;})
    },
    // 清除水印
    destroyWatermark(){
      if (this.div){
        this.div.remove();
        this.div = undefined;
      }
    },
  },
} 
</script>

<style scoped>
  .watermark-container{
    position: relative;
    height: 100%;
  }
</style>